import { TPatientListColumnName, TPatientListTab, TPatientListTabColumn } from 'src/types/patient-list'
import { TCurrentOrder, TPatientListRequest, TPatientListResponse, TTableRow } from './patients.types'
import MedicalAlertList from 'src/components/medical-alert-list/medical-alert-list.component'
import { CurecallList, CurecallListHeaders } from '@honestica/curecall-ui'
import { TItemsPerPage } from 'src/types/others'
import { TNavigationLocales } from 'src/types/locales'
import { useTranslation } from 'react-i18next'
import { getTranslatedLabel } from 'src/utils/locale.util'
import {
  ActionCell,
  ActiveFollowups,
  ConvocationDateCell,
  DateCell,
  DateIntervalCell,
  DoctorCell,
  IdCell,
  IdentityCell,
  MedEventCell,
  PatientCommentsCell,
  TestCell,
  WorkflowCell,
} from './patient-list-cell.component'

export type Props = {
  onTableHeaderClick: ({ tabId, body }: TPatientListRequest) => void
  data: TPatientListResponse | undefined
  patientsPerPage: TItemsPerPage | undefined
  currentTab: TPatientListTab | undefined
  currentOrder: TCurrentOrder | undefined
  currentPage: number | undefined
  isLoading: boolean
  projectId: number
}

export const SORTABLE_COLUMNS_LIST: Array<TPatientListColumnName> = ['PatientId', 'PatientInfo']

export default function PatientListTable({
  onTableHeaderClick,
  patientsPerPage,
  currentOrder,
  currentPage,
  currentTab,
  isLoading,
  projectId,
  data,
}: Readonly<Props>) {
  const { i18n, t } = useTranslation()
  const locale = i18n.language as TNavigationLocales

  const handleSelectTab = (tabColumn: TPatientListTabColumn) => {
    currentTab &&
      onTableHeaderClick({
        tabId: currentTab.id,
        body: {
          patientsPerPage: patientsPerPage ?? 50,
          pageNumber: currentPage ?? 1,
          orderBy: {
            column: tabColumn.PatientListColumn.name,
            descOrder:
              currentOrder?.column === tabColumn.PatientListColumn.name
                ? !currentOrder?.descOrder
                : (currentOrder?.descOrder ?? true),
          },
        },
      })
  }

  const tableRows: Array<TTableRow> =
    data?.patients.map((patient) => ({
      PatientId: (
        <IdCell id={patient.id} isTestAccount={patient.isTestAccount} isDisabled={patient.isArchived} />
      ),
      RemoteId: <IdCell id={patient.remoteId} isTestAccount={patient.isTestAccount} />,
      PatientInfo: <IdentityCell patient={patient} projectId={projectId} />,
      PatientUserInfo:
        patient.isArchived || !patient.PatientMedEvents ? undefined : (
          <DoctorCell patient={patient} selectedService={currentTab} />
        ),
      PatientMedEventInfo:
        patient.isArchived || !patient.PatientMedEvents ? undefined : (
          <MedEventCell patientMedEvent={patient.lastPatientMedEvent} />
        ),
      PatientWorkflowInfo: patient.isArchived ? undefined : (
        <WorkflowCell PatientWorkflows={patient.PatientWorkflows} />
      ),
      PatientFollowups: patient.isArchived ? undefined : (
        <DateCell patientFollowups={patient.PatientFollowups} />
      ),
      PatientPeriodicWorkflows: patient.isArchived ? undefined : (
        <DateCell patientFollowups={patient.PatientFollowups} isPeriodicWorkflow />
      ),
      PatientFollowUpPeriod: patient.isArchived ? undefined : ( // TODO : should be remove because "PatientFollowUpPeriod" (table PatientListColumns) seems to be not used in PatientListTabColumns
        <DateIntervalCell patientFollowups={patient.PatientFollowups} />
      ),
      PatientForms: patient.isArchived ? undefined : <TestCell patientForms={patient.PatientForms} />, // TODO : should be remove because "PatientForms" (table PatientListColumns) seems to be not used in PatientListTabColumns
      PatientActiveFollowups: patient.isArchived ? undefined : (
        <ActiveFollowups className="c-w-[200px]" patientFollowups={patient.PatientFollowups} />
      ),
      PatientActionInfo: <ActionCell projectId={projectId} patient={patient} key={patient.id} />,
      PatientComments: patient.isArchived ? undefined : (
        <PatientCommentsCell hasComment={!!patient.hasMedicalTeamComments} patient={patient} />
      ),
      LastPatientMedEventInfo: patient.isArchived ? undefined : (
        <MedEventCell patientMedEvent={patient.lastPatientMedEvent} />
      ),
      NextPatientMedEventInfo: patient.isArchived ? undefined : (
        <MedEventCell patientMedEvent={patient.nextPatientMedEvent} />
      ),
      LastConvocationDate: patient.isArchived ? undefined : (
        <ConvocationDateCell patientId={patient.id} patientMedEvent={patient.lastPatientMedEvent} />
      ),
      NextConvocationDate: patient.isArchived ? undefined : (
        <ConvocationDateCell patientId={patient.id} patientMedEvent={patient.nextPatientMedEvent} />
      ),
      PatientMedAlertInfo: patient.isArchived ? undefined : (
        <MedicalAlertList
          medicalAlertList={patient.PatientMedAlerts?.filter((pma) => {
            return pma.PatientMedEventId === patient?.lastPatientMedEvent?.id
          })}
          className="c-max-w-[280px]"
          medAlertSize="SMALL"
          showPinnedOnly
        />
      ),
    })) ?? []

  const tableColumns: CurecallListHeaders<TTableRow> =
    currentTab?.PatientListTabColumns.map((tabColumn) => {
      return {
        label: getTranslatedLabel(locale, tabColumn.intlHeader),
        key: tabColumn.PatientListColumn.name,
        onHeaderClick: SORTABLE_COLUMNS_LIST.includes(tabColumn.PatientListColumn.name)
          ? () => handleSelectTab(tabColumn)
          : undefined,
      }
    }) ?? []

  const noDataLabel: string = t('pages.patient-list-page.no-patients', {
    currentTab:
      currentTab?.apiPath !== '/patients' ? t('pages.patient-list-page.no-patients-currentTab') : '',
  })

  return (
    <>
      <CurecallList
        rowsData={tableRows}
        headersData={tableColumns}
        isLoading={isLoading}
        loadingLabel={t('common.actions.loading')}
        noDataLabel={noDataLabel}
        currentOrder={currentOrder?.column && { key: currentOrder.column, descOrder: currentOrder.descOrder }}
      />
    </>
  )
}
